import { DateTime } from '@ksyos/date-time';
import { useEffect, useState } from 'react';
import type { ActivityType } from '../../../../common/enums/activityType';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { ServiceType } from '../../../../common/enums/serviceType';
import { UrlBuilder } from '../../../../common/utils/urlBuilder';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    FormHeader,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { FormatCurrency } from '../../elements/FormatCurrency';

export type IRequestCompensationPayoutListProps = {
    groupId?: string;
    organizationId?: string;
    practitionerId?: string;
    serviceCode?: ServiceCode | null;
    serviceConsumerId?: string;
    serviceGroupSettingId?: string;
    serviceId?: string;
    serviceOrganizationSettingId?: string;
    serviceProviderId?: string;
    serviceSupervisorId?: string;
    serviceType?: ServiceType | null;
};

type ICompensationPayout = {
    amount: number;
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
    endDate: string | Date | null;
    id: string;
    startDate: string | Date;
    type: ActivityType;
};

type IAuthorization = {
    canAdd: boolean;
};

export function RequestCompensationPayoutList(props: IRequestCompensationPayoutListProps) {
    const [compensationPayouts, setCompensationPayouts] = useState<ICompensationPayout[]>([]);
    const [authorization, setAuthorization] = useState<IAuthorization>({ canAdd: false });

    useEffect(() => {
        loadCompensationPayouts();
    }, []);

    async function loadCompensationPayouts() {
        const urlBuilder = new UrlBuilder('/api/compensationPayouts');
        urlBuilder.add('includeListAuthorizations', 'canAdd');
        urlBuilder.add('includeItemAuthorizations', 'canUpdate,canDelete');
        addSpecificPararameter(urlBuilder);
        const result = await fetchJson(urlBuilder.build(), 'GET');
        if (result.json?.items) {
            setCompensationPayouts(result.json.items);
            setAuthorization(result.json.authorization);
        }
    }

    async function remove(id: string) {
        const url = '/api/compensationPayouts/' + id;
        await fetchJson(url, 'DELETE');
        await loadCompensationPayouts();
    }

    function addSpecificPararameter(urlBuilder: UrlBuilder) {
        if (props.serviceSupervisorId) {
            urlBuilder.add('serviceSupervisorId', props.serviceSupervisorId);
        } else if (props.serviceOrganizationSettingId) {
            urlBuilder.add('serviceOrganizationSettingId', props.serviceOrganizationSettingId);
        } else if (props.serviceProviderId) {
            urlBuilder.add('serviceProviderId', props.serviceProviderId);
        } else if (props.serviceConsumerId) {
            urlBuilder.add('serviceConsumerId', props.serviceConsumerId);
        } else if (props.serviceGroupSettingId) {
            urlBuilder.add('serviceGroupSettingId', props.serviceGroupSettingId);
        }
    }

    function getUrlWithParams(baseUrl: string) {
        const urlBuilder = new UrlBuilder(baseUrl);
        addSpecificPararameter(urlBuilder);
        if (props.serviceType) {
            urlBuilder.add('serviceType', props.serviceType);
        }
        if (props.serviceCode) {
            urlBuilder.add('serviceCode', props.serviceCode);
        }

        if (props.serviceId) {
            urlBuilder.add('serviceId', props.serviceId);
        }

        if (props.groupId) {
            urlBuilder.add('groupId', props.groupId);
        }

        if (props.practitionerId) {
            urlBuilder.add('practitionerId', props.practitionerId);
        }

        if (props.organizationId) {
            urlBuilder.add('organizationId', props.organizationId);
        }

        return urlBuilder.build();
    }

    let actionButtons: JSX.Element[] | undefined;
    if (authorization.canAdd) {
        actionButtons = [
            <LinkButton
                icon="add"
                to={getUrlWithParams('/compensationPayouts/add')}
            >
                {translateKey('common.add')}
            </LinkButton>,
        ];
    }

    return (
        <Loading
            item={compensationPayouts}
            loaded={(compensations) => (
                <>
                    <FormHeader actionButtons={actionButtons}>{translateKey('compensationPayouts.fees')}</FormHeader>
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                <TableHeader>{translateKey('compensationPayouts.amount')}</TableHeader>
                                <TableHeader>{translateKey('common.activity')}</TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {compensations.map((compensation, key) => (
                                <TableRow key={key}>
                                    <TableData>{DateTime.from(compensation.startDate).format('DD-MM-YYYY')}</TableData>
                                    <TableData>
                                        {compensation.endDate
                                            ? DateTime.from(compensation.endDate).format('DD-MM-YYYY')
                                            : ''}
                                    </TableData>
                                    <TableData>
                                        <FormatCurrency value={compensation.amount} />
                                    </TableData>
                                    <TableData>{translateKey(`activityTypeEnum.${compensation.type}`)}</TableData>
                                    {compensation.authorization.canUpdate && (
                                        <TableData>
                                            <EditButton
                                                to={getUrlWithParams(`/compensationPayouts/${compensation.id}`)}
                                            />
                                            {compensation.authorization.canDelete && (
                                                <DeleteButton onClick={() => remove(compensation.id)} />
                                            )}
                                        </TableData>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </AdminTable>
                </>
            )}
        />
    );
}
